import React, { useState, useEffect } from 'react';
import moment from 'moment'; 
import { updateTimesheetStatus } from '../../../../../repository/TimeSheetDAO';
import { useOrganizationContext } from '../../../../../context/organization_context/OrganizationContext';
import { useLoadingContext } from '../../../../../context/loading/LoadingContext';
import { useToast, ToastType } from '../../../../../context/toast/ToastContext';
import BaseModal from '../../../../../components/modals/BaseModal';
import { Table, TableBody, TableCell, TableRow, TableHead, TableContainer, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../../../resources/strings/StringKeys';

const ApproveModal = ({ show, onHide, timesheet }) => {
    const [days, setDays] = useState([]);
    const [jobCodes, setJobCodes] = useState({});
    const [totalHours, setTotalHours] = useState(0);
    const { organization } = useOrganizationContext()
    const { setLoadingData } = useLoadingContext()
    const { showToast } = useToast()
    const { t } = useTranslation();

    useEffect(() => {
        if (timesheet) {
            extractDaysAndJobCodes(timesheet);
        }
    }, [timesheet]);

    const extractDaysAndJobCodes = (timesheet) => {
        const days = Object.keys(timesheet.daily_hours).sort();
        const jobCodes = {};
        let total = 0; 

        days.forEach(day => {
            Object.keys(timesheet.daily_hours[day]).forEach(jobCode => {
                jobCodes[jobCode] = jobCodes[jobCode] || [];
                const hours = timesheet.daily_hours[day][jobCode];
                jobCodes[jobCode].push({ date: day, hours });
                total += hours; 
            });
        });

        setDays(days);
        setJobCodes(jobCodes);
        setTotalHours(total);
    };

    const renderTableHeader = () => {
        return (
            <TableRow>
                <TableCell style={{fontWeight:"bold"}}>{t(StringKeys.JOB_CODE)}</TableCell>
                {days.map(day => <TableCell style={{fontWeight:"bold"}} key={day}>{moment(day).format('MM/DD')}</TableCell>)}
                <TableCell style={{fontWeight:"bold"}}>{t(StringKeys.TOTAL)}</TableCell>
            </TableRow>
        );
    };

    const renderTableRows = () => {
        return Object.entries(jobCodes).map(([code, entries], index) => (
            <TableRow key={index}>
                <TableCell>{code}</TableCell>
                {days.map(day => (
                    <TableCell key={day}>
                        {entries.find(entry => entry.date === day)?.hours || 0}
                    </TableCell>
                ))}
                <TableCell>{entries.reduce((acc, cur) => acc + cur.hours, 0)}</TableCell>
            </TableRow>
        ));
    };

    const handleSubmit = async (status) => {
        setLoadingData(true);
        try {
            const result = await updateTimesheetStatus(organization.id, timesheet.id, status);
    
            if (result.success) {
                showToast(t(StringKeys.SUCCESS), ToastType.SUCCESS);
            } else {
                showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
                console.error('Error updating timesheet status:', result.error);
            }
        } catch (error) {
            console.error('Unexpected error updating timesheet status:', error);
            showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
        } finally {
            setLoadingData(false);
            resetForm();
            onHide();
        }
    };    

    const resetForm = () => {
        setDays([])
        setJobCodes({})
        setTotalHours(0)
    };

    // Early return if timesheet is not provided
    if (!timesheet) return null;

    const actions = (
        <>
            <Button variant="outlined" color="error" onClick={() => handleSubmit('Rejected')}>{t(StringKeys.REJECT)}</Button>
            <Button variant="contained" color="success" onClick={() => handleSubmit('Approved')} style={{ marginLeft: '10px' }}>{t(StringKeys.APPROVE)}</Button>
        </>
    );

    return (
        <BaseModal
            open={show}
            onClose={onHide}
            title={"Approvals"}
            actions={actions}
        >
            <TableContainer>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                    {renderTableHeader()}
                </TableHead>
                <TableBody>
                    {renderTableRows()}
                </TableBody>
            </Table>
            </TableContainer>
            <div style={{ textAlign: 'right', marginTop: '20px', fontWeight: 'bold' }}>
                {t(StringKeys.TOTAL_HOURS)}: {totalHours}
            </div>
        </BaseModal>
    );
};

export default ApproveModal;
